import esriConfig from '@arcgis/core/config.js';
import { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createLayerFromUrl } from '@rsos/maps/providers/esri/layers/createLayerFromUrl';
import { createWMSLayer } from '@rsos/maps/providers/esri/layers/createWMSLayer';
import { createWMTSLayer } from '@rsos/maps/providers/esri/layers/createWMTSLayer';
import { addSupplementalServicesSublayers } from '@rsos/reduxDataStore/src/features/gisServices/gisServicesSlice';
import checkGISServicesAccess from '@rsos/utils/checkGISServicesAccess';
import usePortalType from '@rsos/utils/usePortalType';
import usePrevious from '@rsos/utils/usePrevious';
import {
  TRAFFIC_LAYER_URL,
  NA_TRAFFIC_LAYER_NAME,
} from '../constants/gisSupplementalLayer';
import { getGISLayerID } from '../utils/getGISLayerID';
import {
  getNestedFeatureLayer,
  getNestedMapImageLayer,
} from '../utils/getNestedLayers';
import useGISHelpers from './useGISHelpers';

const VERMONT_SUPPLEMENTAL_LAYER_URL =
  'https://vermont.geocomm.cloud/arcgis/rest/services/vermont/Supplemental/MapServer';

/**
 * Request interceptor handler when creating a new GIS service layer from url.
 * @param {String} url - Url to incercept.
 * @param {String} token - Token parameter to be added to the url.
 * @returns Array
 */
const handleRequestInterceptor = (url, token) => {
  esriConfig.request.interceptors.push({
    urls: url,
    before: params => {
      params.requestOptions.query = params.requestOptions.query || {};

      params.requestOptions.query.token = token;
    },
    error: () => {
      // Suppress request interceptor error.
    },
  });
};

/**
 * An Esri map GIS services layers hook that processes GIS supplemental, WMS, and WMTS services.
 * GIS map layers are controlled via the MapLayersControl component.
 * @param {Object} map - The Esri map object
 * @returns {Array} An array of all processed GIS service layers.
 */
const useGISServiceLayers = map => {
  const { isPremiumUser } = usePortalType();

  const dispatch = useDispatch();

  const { hasEsriMapAccess } = useGISHelpers();

  const hasGISServicesAccess =
    useSelector(state =>
      checkGISServicesAccess({
        psaps: state.psaps,
        sinatra: state.sinatra,
      }),
    ) &&
    isPremiumUser &&
    hasEsriMapAccess;

  const basemapService = useSelector(state => state.gisServices.basemapService);

  const supplementalServices = useSelector(
    state => state.gisServices.supplementalServices,
  );

  const wmsServices = useSelector(state => state.gisServices.wmsServices);

  const wmtsServices = useSelector(state => state.gisServices.wmtsServices);

  const activeGISServices = useSelector(
    state => state.gisServices.activeGISServices,
  );

  const supplementalServicesSublayers = useSelector(
    state => state.gisServices.supplementalServicesSublayers,
  );

  const lastToggledGISService = useSelector(
    state => state.gisServices.lastToggledGISService,
  );

  const numClearAllClicked = useSelector(
    state => state.gisServices.numClearAllClicked,
  );

  const mapInstance = map?.mapInstance?.map;

  const esriMapLayers = mapInstance?.layers?.items;

  const [supplementalLayers, setSupplementalLayers] = useState([]);
  const previousSupplementalLayersLength = usePrevious(
    supplementalLayers?.length || 0,
  );

  const [wmsLayers, setWMSLayers] = useState([]);

  const [wmtsLayers, setWMTSLayers] = useState([]);

  /**
   * =======================================
   *  GIS SUPPLEMENTAL LAYERS CONFIGURATION
   * =======================================
   */

  // Create GIS supplemental layers on load and set the visible property to false.
  // The visible property is updated when the user toggles the layer via the map layers menu control.
  useEffect(() => {
    if (hasEsriMapAccess && supplementalServices.length) {
      const createSupplementalLayers = async () => {
        const initialPromises = supplementalServices.map(service => {
          handleRequestInterceptor(service.gis_service_url, service.token);

          const layerData = {
            apiKey: service?.token,
            id: getGISLayerID(service),
            url: service.gis_service_url,
            visible: service.auto_checked || false,
            outFields: ['*'],
            opacity: 0.4,
          };

          return createLayerFromUrl(layerData)
            .then(layer => {
              if (layer.url === TRAFFIC_LAYER_URL) {
                const northAmericaTrafficLayer = layer.sublayers.items.find(
                  sublayer => sublayer.title === NA_TRAFFIC_LAYER_NAME,
                );
                const northAmericaSublayers = getNestedMapImageLayer(
                  northAmericaTrafficLayer,
                  service,
                  layerData,
                );
                return northAmericaSublayers;
              } else if (layer.url === VERMONT_SUPPLEMENTAL_LAYER_URL) {
                // this is a special case for Vermont Supplemental service
                // Geocomm has agreed to turn on the sublayers for other layers as well but that would take some time on their end
                if (layer?.loaded) {
                  return { layer, service };
                }
                return null;
              } else {
                if (layer?.loaded) {
                  if (
                    service.attributes.gis_service_category === 'map_service'
                  ) {
                    return getNestedMapImageLayer(layer, service, layerData);
                  }
                  // Feature layers
                  return getNestedFeatureLayer(layer, service, layerData);
                }
              }
              return null;
            })
            .catch(() => {
              // fail silently
              return null;
            });
        });

        const intermediateResults = await Promise.all(initialPromises);

        const finalLayerPromises = intermediateResults.flatMap(result => {
          if (result === null) {
            return []; // Filter out nulls
          }
          if (Array.isArray(result)) {
            return result;
          }
          return [result];
        });

        const resolvedLayerResults = await Promise.all(finalLayerPromises);

        const layers = resolvedLayerResults.filter(result => result !== null);
        setSupplementalLayers(layers);
      };

      createSupplementalLayers();
    }
  }, [hasEsriMapAccess, supplementalServices]);

  /**
   * ==========================
   *  WMS LAYERS CONFIGURATION
   * ==========================
   */

  // Create GIS WMS layers on load and set the visible property to false.
  // The visible property is updated when the user toggles the layer via the map layers menu control.
  useEffect(() => {
    if (hasEsriMapAccess && wmsServices.length) {
      const layers = [];

      wmsServices.forEach(service => {
        const layerData = {
          id: getGISLayerID(service),
          layerNames: service?.attributes?.sublayers || '',
          url: service.gis_service_url,
          visible: false,
        };

        createWMSLayer(layerData)
          .then(layer => {
            if (layer?.loaded && !layers.includes(layer)) {
              layers.push({ layer, service });
            }
          })
          .catch(() => {
            // fail silently
          });
      });

      setWMSLayers(layers);
    }
  }, [hasEsriMapAccess, wmsServices]);

  /**
   * ===========================
   *  WMTS LAYERS CONFIGURATION
   * ===========================
   */

  // Create GIS WMTS layers on load and set the visible property to false.
  // The visible property is updated when the user toggles the layer via the map layers menu control.
  useEffect(() => {
    if (hasEsriMapAccess && wmtsServices.length) {
      const layers = [];

      wmtsServices.forEach(service => {
        // Unlike WMS layers which accepts an array of layer names, WMTS layer accepts a single
        // layer name that can be displayed on the map.
        const layerData = {
          id: getGISLayerID(service),
          layerName: service?.attributes?.sublayers?.[0] || '',
          url: service.gis_service_url,
          visible: false,
        };

        createWMTSLayer(layerData)
          .then(layer => {
            if (layer?.loaded && !layers.includes(layer)) {
              layers.push({ layer, service });
            }
          })
          .catch(() => {
            // fail silently
          });
        // bug? only sets the last WMTS layer
        setWMTSLayers(layers);
      });
    }
  }, [hasEsriMapAccess, wmtsServices]);

  // When an user toggles a GIS service layer via the map layers menu control, update the layer's
  // visible property.
  // https://developers.arcgis.com/javascript/latest/api-reference/esri-Map.html#findLayerById
  useEffect(() => {
    if (
      hasEsriMapAccess &&
      mapInstance &&
      activeGISServices &&
      Object.keys(lastToggledGISService).length
    ) {
      const layerID = getGISLayerID(lastToggledGISService.layer);
      const visible = lastToggledGISService.visible;
      const isSublayer = lastToggledGISService.isSublayer;
      if (isSublayer) {
        mapInstance.allLayers.items.find(layer => {
          if (layer.id === lastToggledGISService.layer.sublayerID) {
            layer.visible = visible;
          }
        });
      } else {
        mapInstance.allLayers.items.forEach(layer => {
          if (layer.id.startsWith(layerID)) {
            layer.visible = visible;
          }
        });
      }
    }
  }, [
    hasEsriMapAccess,
    mapInstance,
    activeGISServices,
    lastToggledGISService,
    supplementalServicesSublayers,
  ]);

  // If the clear all button was click in the map layers control,
  // set all the GIS supplemental service layers' visibility to false.
  useEffect(() => {
    if (hasGISServicesAccess && esriMapLayers && numClearAllClicked) {
      esriMapLayers.forEach(layer => {
        if (layer.id.startsWith(`gis-service`)) {
          layer.visible = false;
        }
      });
    }
  }, [
    dispatch,
    hasGISServicesAccess,
    esriMapLayers,
    basemapService,
    numClearAllClicked,
  ]);

  useEffect(() => {
    if (supplementalLayers?.length > previousSupplementalLayersLength) {
      const sublayerServices = supplementalLayers.map(layer => {
        return layer.service;
      });
      dispatch(addSupplementalServicesSublayers(sublayerServices));
    }
  }, [dispatch, previousSupplementalLayersLength, supplementalLayers]);

  return useMemo(
    () => [...supplementalLayers, ...wmsLayers, ...wmtsLayers],
    [supplementalLayers, wmsLayers, wmtsLayers],
  );
};

export default useGISServiceLayers;
